import React from "react";
import { Link, useLocation } from "react-router-dom";
import LoopsPopup from "./LoopsPopup";
import { useAuth } from "../contexts/AuthContext";

const SOCIAL_LINKS = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61587364958919",
    ariaLabel: "Realign Mind on Facebook",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/mindrealign/",
    ariaLabel: "Realign Mind on Instagram",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.027.06 1.381.06 3.808 0 2.43-.013 2.784-.06 3.808-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.027.048-1.381.06-3.808.06s-2.784-.013-3.808-.06c-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.048-1.027-.06-1.381-.06-3.808 0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772 4.902 4.902 0 011.772-1.153c.636-.247 1.363-.416 2.427-.465C9.536 2.013 9.89 2 12.315 2zM12 6.865a5.135 5.135 0 100 10.27 5.135 5.135 0 000-10.27zm0 8.468a3.333 3.333 0 100-6.666 3.333 3.333 0 000 6.666zm5.338-3.205a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
];

function Footer() {
  const location = useLocation();
  const { isSubscribed } = useAuth();
  return (
    <footer className="bg-background text-text border-t border-tint">
      <div className="max-w-screen-xl mx-auto px-4 py-8">
        {/* Newsletter Section — hidden for paying subscribers */}
        {!isSubscribed && (
          <div className="mb-6 pb-6 border-b border-tint">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h3 className="text-lg font-semibold text-text mb-1">
                  Get the free email series
                </h3>
                <p className="text-sm text-textSecondary">
                  Seven emails on the science behind bilateral stimulation — and how to know if it's right for you.
                </p>
              </div>
              <div className="flex-shrink-0">
                <LoopsPopup
                  buttonText="Send me the series"
                  className="px-6 py-3 bg-accent hover:bg-accentDark text-white font-semibold rounded-lg transition-colors"
                  source="footer"
                />
              </div>
            </div>
          </div>
        )}

        {/* Site Navigation */}
        <div className="mb-6 pb-6 border-b border-tint">
          <nav>
            <ul className="flex flex-wrap justify-center gap-6 md:gap-8">
              <li>
                <a
                  href="/what-is-bilateral-stimulation"
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  What is Bilateral Stimulation?
                </a>
              </li>
              <li>
                <a
                  href="/how-it-works"
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  The Science
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  About Us
                </a>
              </li>
              <li>
                <Link
                  to="/demo"
                  state={{ from: location.pathname }}
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  Try the Demo
                </Link>
              </li>
              <li>
                <a
                  href="/subscribe"
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  Start Free Trial
                </a>
              </li>
              <li>
                <a
                  href="https://blog.realignmind.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-text hover:text-accent transition-colors"
                >
                  Blog
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* Legal Links and Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-sm text-textSecondary">
              &copy; {new Date().getFullYear()} Realign LLC. All rights reserved.
            </p>
            <nav className="flex items-center gap-3" aria-label="Social media">
              {SOCIAL_LINKS.map(({ name, href, ariaLabel, icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-textSecondary hover:text-accent transition-colors"
                  aria-label={ariaLabel}
                >
                  {icon}
                </a>
              ))}
            </nav>
          </div>
          <nav>
            <ul className="flex flex-wrap justify-center gap-4 md:gap-6">
              <li>
                <a
                  href="/terms-of-service"
                  className="text-sm text-primary hover:text-accent transition-colors"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  className="text-sm text-primary hover:text-accent transition-colors"
                >
                  Contact
                </a>
              </li>
              <li>
                <a
                  href="/disclaimer"
                  className="text-sm text-primary hover:text-accent transition-colors"
                >
                  Disclaimer
                </a>
              </li>
              <li>
                <a
                  href="/emergency-support"
                  className="text-sm text-primary hover:text-accent transition-colors"
                >
                  Emergency Support
                </a>
              </li>
              <li>
                <a
                  href="/privacy-policy"
                  className="text-sm text-primary hover:text-accent transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
